import { useEffect } from 'react';
import { useLocation, useHistory } from 'react-router';
import { useStoreActions, useOverlayContext } from '~/contexts';
import { ServerError } from '~/infra/ErrorResponse';
import { handleRequest } from '~/infra/utils';
import { EmailOnBehalfTypes } from '~/models/EmailOnBehalfSettingsModel';
import { getEmailProviderDetails } from '~/pages/NotificationSettings/NotificationSettings.utils';
const getProviderSpecificError = (provider, error)=>{
    if (provider === 'gmail') {
        const isMissingRequiredScopes = error.hasServerError(ServerError.InvalidScopeMissingUserInfo) || error.hasServerError(ServerError.InvalidScopeMissingGmailSend);
        if (isMissingRequiredScopes) {
            return {
                errorMessage: 'Mailbox connection failed - required permissions were not granted',
                toastId: 'complete-gmail-auth-error-toast'
            };
        }
    } else if (provider === 'outlook') {
        if (error.hasServerError(ServerError.PersonalAccountNotSupported)) {
            return {
                errorMessage: "Personal Outlook accounts aren't supported",
                toastId: 'complete-outlook-personal-account-error-toast'
            };
        }
    }
    return {
        errorMessage: "Failed to connect ".concat(provider, " account - please try again"),
        toastId: "complete-".concat(provider, "-auth-generic-error-toast")
    };
};
export const useEmailAuthCompletion = (param)=>{
    let { businessId, onSuccess, onError, onAccessDenied } = param;
    const location = useLocation();
    const history = useHistory();
    const { api, logBI } = useStoreActions();
    const { startLoader, stopLoader, showErrorToast } = useOverlayContext();
    useEffect(()=>{
        const params = new URLSearchParams(location.search);
        const code = params.get('code');
        const state = params.get('state');
        const paramError = params.get('error');
        if (!code && !paramError) return;
        const handleEmailAuth = async (provider)=>{
            const scope = params.get('scope');
            const csrfToken = state === null || state === void 0 ? void 0 : state.split(':')[1];
            // both code and businessId are required in gmail and outlook endpoints
            if (!code || !businessId) return;
            startLoader();
            const providerDetails = getEmailProviderDetails(provider);
            let apiCall;
            if (provider === 'gmail') {
                if (!scope) return;
                apiCall = api.notification.emailOnBehalf.completeGmailAuthorization({
                    businessId,
                    code,
                    scope,
                    csrfToken,
                    redirectUri: window.location.origin + window.location.pathname
                });
            } else {
                apiCall = api.notification.emailOnBehalf.completeOutlookAuthorization({
                    businessId,
                    code,
                    csrfToken,
                    redirectUri: window.location.origin + window.location.pathname
                });
            }
            const { success, error } = await handleRequest(apiCall);
            if (success) {
                logBI(providerDetails.biCodes.completeAuth, {
                    vendorId: businessId
                });
                await (onSuccess === null || onSuccess === void 0 ? void 0 : onSuccess());
            } else {
                logBI(providerDetails.biCodes.authError, {
                    vendorId: businessId
                });
                const { errorMessage, toastId } = getProviderSpecificError(provider, error);
                if (onError) {
                    onError(errorMessage, toastId);
                } else {
                    showErrorToast(errorMessage, toastId);
                }
            }
            history.replace(location.pathname);
            stopLoader();
        };
        const handleAuth = async ()=>{
            if (paramError === 'access_denied') {
                onAccessDenied === null || onAccessDenied === void 0 ? void 0 : onAccessDenied();
                history.replace(location.pathname);
                return;
            }
            const stateProvider = state ? state.split(':')[0] : null;
            if (stateProvider === 'outlook') {
                handleEmailAuth(EmailOnBehalfTypes.Outlook);
            } else if (stateProvider === 'gmail') {
                handleEmailAuth(EmailOnBehalfTypes.Gmail);
            }
        };
        handleAuth();
    }, [
        businessId,
        location.pathname,
        location.search
    ]);
};
